import React, { useEffect, useState } from 'react';
import {
  Card,
  Row,
  CardTitle,
  CardBody,
  Button,
  FormGroup,
  Label,
  Input,
  FormText,
} from 'reactstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { useSelector } from 'react-redux';
import { apiService } from '../../constants/ApiService';
import { toast } from 'react-toastify';
import useVendorBranch from '../../utils/useVendorBranch';

const dayOptions = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 7, label: 'Sunday' },
];

const AddEditTimeTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.authState);

  const { isSuperAdmin, isAdmin, hasMultiBranch } = useVendorBranch(user);

  const [timeTableData, setTimeTableData] = useState({
    id: 0,
    vendor_id: 0,
    branch_id: 0,

    class_id: '',
    batch_id: '',
    day_id: '',
    start_time: '',
    end_time: '',
    subject_id: '',
    faculty_id: '',

    class_name: '',
    batch_name: '',
    subject_name: '',
    day_name: '',
    faculty_name: '',
  });

  const [errorMessage, setErrorMessage] = useState({});
  const [classList, setClassList] = useState([]);
  const [batchList, setBatchList] = useState([]);
  const [facultyList, setFacultyList] = useState([]);
  const [subjectList, setSubjectList] = useState([]);

  useEffect(() => {
    if (location.state) {
      setTimeTableData((prev) => ({
        ...prev,
        ...location.state,
        start_time: location.state.start_time?.substring?.(0, 5) || '',
        end_time: location.state.end_time?.substring?.(0, 5) || '',
      }));
    }
  }, [location.state]);

  const mapDropdownData = (raw, idKeys = [], nameKeys = []) => {
    return (raw || [])
      .map((x) => {
        const id =
          idKeys
            .map((k) => x?.[k])
            .find((v) => v !== undefined && v !== null && v !== '') ?? 0;

        const name =
          nameKeys
            .map((k) => x?.[k])
            .find((v) => v !== undefined && v !== null && v !== '') ?? '';

        return {
          value: String(id),
          label: String(name),
          raw: x,
        };
      })
      .filter((x) => x.value !== '0' && x.label);
  };

  const toTimeSpanString = (value) => {
    if (!value) return null;
    return value.length === 5 ? `${value}:00` : value;
  };

  const fetchClasses = async (searchText = '') => {
    try {
      const vendorId = timeTableData.vendor_id || '';
      const branchId = timeTableData.branch_id || '';

      const response = await apiService.get(
        `api/ClassMaster/GetClassMaster?vendorId=${vendorId}&branchId=${branchId}&searchItem=${encodeURIComponent(
          searchText || '',
        )}`,
      );

      const raw = response?.data || [];
      const mapped = mapDropdownData(
        raw,
        ['class_id', 'id', 'ID'],
        ['class_Name', 'class_name', 'name', 'Name', 'className'],
      );

      setClassList(mapped);
    } catch (error) {
      console.log(error);
      toast.error('Failed to load class');
    }
  };

  const fetchFaculty = async (searchText = '') => {
    try {
      const vendorId = timeTableData.vendor_id || '';
      const branchId = timeTableData.branch_id || '';

      const res = await apiService.get(
        `api/FacultyMaster/GetAllFaculty?vendorId=${vendorId}&branchId=${branchId}&search=${encodeURIComponent(
          searchText || '',
        )}`,
      );

      if (res?.status === true) {
        const facultyRaw = res?.data || [];

        setFacultyList(
          facultyRaw
            .map((x) => {
              const id = x.faculty_id ?? x.id ?? x.ID ?? 0;
              const name =
                x.faculty_name ?? x.name ?? x.Name ?? x.facultyName ?? '';
              return { value: String(id), label: String(name), raw: x };
            })
            .filter((x) => x.value !== '0' && x.label),
        );
      } else {
        toast.error(res?.message || 'Failed to fetch faculty');
      }
    } catch (err) {
      console.log(err);
      toast.error('Failed to load faculty');
    }
  };

  const fetchSubjects = async (search = '') => {
    if (search == null) search = '';

    try {
      const vendorId = timeTableData.vendor_id || '';
      const branchId = timeTableData.branch_id || '';

      const config = {
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
      };

      const url = `api/SubjectMaster/GetSubjectMaster?searchItem=${encodeURIComponent(
        search,
      )}&vendorId=${vendorId}&branchId=${branchId}`;

      const response = await apiService.get(url, config);

      if (response.response && response.response.data?.errors) {
        alert(response.response?.data?.errors);
      }

      if (response.status === true) {
        const raw = response.data || [];
        const mapped = mapDropdownData(
          raw,
          ['subject_id', 'id', 'ID'],
          ['subject_name', 'name', 'Name', 'subjectName'],
        );
        setSubjectList(mapped);
      } else {
        setSubjectList([]);
        console.error(response.message);
      }
    } catch (error) {
      console.log(error);
      alert('Something went wrong');
      setSubjectList([]);
    }
  };

  const getBatchListData = async (classIdValue, search = '') => {
    try {
      const vendorId = timeTableData.vendor_id || '';
      const branchId = timeTableData.branch_id || '';

      const response = await apiService.get(
        `api/BatchMaster/GetAllBatch?classId=${classIdValue || ''}&vendorId=${vendorId}&branchId=${branchId}&search=${encodeURIComponent(
          search || '',
        )}`,
      );

      if (response.response && response.response.data?.errors) {
        toast.error(response.response.data.errors);
      }

      if (response.status === true) {
        const raw = response.data || [];
        const mapped = mapDropdownData(
          raw,
          ['batch_id', 'id', 'ID'],
          ['batch_name', 'name', 'Name', 'batchName'],
        );
        setBatchList(mapped);
      } else {
        toast.error(response.message || 'Failed to fetch batch');
      }
    } catch (error) {
      console.log(error);
      toast.error('Failed to load batch');
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchFaculty();
    fetchSubjects();

    if (timeTableData.class_id) {
      getBatchListData(timeTableData.class_id);
    } else {
      setBatchList([]);
    }
  }, [
    timeTableData.vendor_id,
    timeTableData.branch_id,
    timeTableData.class_id,
  ]);

  const onChangeHandle = (e) => {
    const { name, value } = e.target;

    setErrorMessage((prev) => ({ ...prev, [name]: '' }));

    if (name === 'day_id') {
      const selectedDay = dayOptions.find(
        (x) => String(x.value) === String(value),
      );

      setTimeTableData((prev) => ({
        ...prev,
        day_id: value,
        day_name: selectedDay?.label || '',
      }));
      return;
    }

    setTimeTableData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onClassChange = async (e) => {
    const value = e.target.value;
    const selected = classList.find((x) => x.value === String(value));

    setTimeTableData((prev) => ({
      ...prev,
      class_id: value,
      class_name: selected?.label || '',
      batch_id: '',
      batch_name: '',
    }));

    setErrorMessage((prev) => ({
      ...prev,
      class_id: '',
      batch_id: '',
    }));

    await getBatchListData(value);
  };

  const onBatchChange = (e) => {
    const value = e.target.value;
    const selected = batchList.find((x) => x.value === String(value));

    setTimeTableData((prev) => ({
      ...prev,
      batch_id: value,
      batch_name: selected?.label || '',
    }));

    setErrorMessage((prev) => ({ ...prev, batch_id: '' }));
  };

  const onSubjectChange = (e) => {
    const value = e.target.value;
    const selected = subjectList.find((x) => x.value === String(value));

    setTimeTableData((prev) => ({
      ...prev,
      subject_id: value,
      subject_name: selected?.label || '',
    }));

    setErrorMessage((prev) => ({ ...prev, subject_id: '' }));
  };

  const onFacultyChange = (e) => {
    const value = e.target.value;
    const selected = facultyList.find((x) => x.value === String(value));

    setTimeTableData((prev) => ({
      ...prev,
      faculty_id: value,
      faculty_name: selected?.label || '',
    }));

    setErrorMessage((prev) => ({ ...prev, faculty_id: '' }));
  };

  const validate = () => {
    const errors = {};

    if (isSuperAdmin && !timeTableData.vendor_id) {
      toast.error('Please select vendor');
      return false;
    }

    if (
      (isSuperAdmin || isAdmin || hasMultiBranch) &&
      !timeTableData.branch_id
    ) {
      toast.error('Please select branch');
      return false;
    }

    if (!timeTableData.class_id) errors.class_id = 'Class is required';
    if (!timeTableData.batch_id) errors.batch_id = 'Batch is required';
    if (!timeTableData.day_id) errors.day_id = 'Day is required';
    if (!timeTableData.subject_id) errors.subject_id = 'Subject is required';
    if (!timeTableData.faculty_id) errors.faculty_id = 'Faculty is required';
    if (!timeTableData.start_time) errors.start_time = 'Start time is required';
    if (!timeTableData.end_time) errors.end_time = 'End time is required';

    if (
      timeTableData.start_time &&
      timeTableData.end_time &&
      timeTableData.end_time <= timeTableData.start_time
    ) {
      errors.end_time = 'End time must be greater than start time';
    }

    setErrorMessage(errors);
    return Object.keys(errors).length === 0;
  };

  const buildPayload = () => ({
    id: timeTableData.id || 0,
    vendor_id: timeTableData.vendor_id ? Number(timeTableData.vendor_id) : null,
    branch_id: timeTableData.branch_id ? Number(timeTableData.branch_id) : null,

    class_id: timeTableData.class_id ? Number(timeTableData.class_id) : null,
    batch_id: timeTableData.batch_id ? Number(timeTableData.batch_id) : null,
    day_id: timeTableData.day_id ? Number(timeTableData.day_id) : null,
    start_time: toTimeSpanString(timeTableData.start_time),
    end_time: toTimeSpanString(timeTableData.end_time),
    subject_id: timeTableData.subject_id
      ? Number(timeTableData.subject_id)
      : null,
    faculty_id: timeTableData.faculty_id
      ? Number(timeTableData.faculty_id)
      : null,

    class_name: timeTableData.class_name || '',
    batch_name: timeTableData.batch_name || '',
    subject_name: timeTableData.subject_name || '',
    day_name: timeTableData.day_name || '',
    faculty_name: timeTableData.faculty_name || '',
  });

  const SaveData = async () => {
    if (!validate()) return;

    try {
      const payload = buildPayload();
      let response;

      if (timeTableData.id > 0) {
        response = await apiService.put(
          `api/TimeTableMaster/UpdateTimeTable?id=${timeTableData.id}`,
          payload,
        );
      } else {
        response = await apiService.post(
          'api/TimeTableMaster/AddTimeTable',
          payload,
        );
      }

      if (response.status === true) {
        toast.success(response.message);
        navigate('/list-class-routine');
      } else {
        toast.error(response.message || 'Save failed');
      }
    } catch (err) {
      console.error(err);

      const apiErrors = err?.response?.data?.errors;
      if (apiErrors) {
        const firstError = Object.values(apiErrors)?.flat?.()?.[0];
        toast.error(firstError || 'Validation failed');
      } else {
        toast.error(err?.response?.data?.message || 'Something went wrong');
      }
    }
  };

  const DeleteData = async () => {
    if (!timeTableData.id) return;
    if (!window.confirm('Delete this record?')) return;

    try {
      const response = await apiService.delete(
        `api/TimeTableMaster/DeleteTimeTable?id=${timeTableData.id}`,
      );

      if (response.status === true) {
        toast.success(response.message);
        navigate('/list-class-routine');
      } else {
        toast.error(response.message || 'Delete failed');
      }
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong');
    }
  };

  return (
    <Card className='card-box pd-20 mb-20'>
      <CardTitle className='border-bottom p-3 d-flex justify-content-between'>
        <span>Time Table Form</span>
        <div>
          <Button color='warning' onClick={() => navigate(-1)}>
            Back
          </Button>

          {timeTableData.id > 0 && (
            <Button color='danger' className='ml-2' onClick={DeleteData}>
              Delete
            </Button>
          )}
        </div>
      </CardTitle>

      <CardBody>
        <VendorBranchSelector
          user={user}
          selectionData={timeTableData}
          setSelectionData={setTimeTableData}
          errorMessage={errorMessage}
          accessButton={true}
        />

        <Row className='mt-3'>
          <FormGroup className='col-md-4'>
            <Label>Class</Label>
            <Input
              type='select'
              className='custom-select'
              value={timeTableData.class_id}
              onChange={onClassChange}
            >
              <option value=''>Select Class</option>
              {classList.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </Input>
            <FormText color='danger'>{errorMessage.class_id}</FormText>
          </FormGroup>

          <FormGroup className='col-md-4'>
            <Label>Batch</Label>
            <Input
              type='select'
              className='custom-select'
              value={timeTableData.batch_id}
              onChange={onBatchChange}
              disabled={!timeTableData.class_id}
            >
              <option value=''>Select Batch</option>
              {batchList.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </Input>
            <FormText color='danger'>{errorMessage.batch_id}</FormText>
          </FormGroup>

          <FormGroup className='col-md-4'>
            <Label>Day</Label>
            <Input
              type='select'
              className='custom-select'
              name='day_id'
              value={timeTableData.day_id}
              onChange={onChangeHandle}
            >
              <option value=''>Select Day</option>
              {dayOptions.map((x) => (
                <option key={x.value} value={x.value}>
                  {x.label}
                </option>
              ))}
            </Input>
            <FormText color='danger'>{errorMessage.day_id}</FormText>
          </FormGroup>

          <FormGroup className='col-md-4'>
            <Label>Start Time</Label>
            <Input
              type='time'
              name='start_time'
              value={timeTableData.start_time}
              onChange={onChangeHandle}
            />
            <FormText color='danger'>{errorMessage.start_time}</FormText>
          </FormGroup>

          <FormGroup className='col-md-4'>
            <Label>End Time</Label>
            <Input
              type='time'
              name='end_time'
              value={timeTableData.end_time}
              onChange={onChangeHandle}
            />
            <FormText color='danger'>{errorMessage.end_time}</FormText>
          </FormGroup>

          <FormGroup className='col-md-4'>
            <Label>Subject</Label>
            <Input
              type='select'
              className='custom-select'
              value={timeTableData.subject_id}
              onChange={onSubjectChange}
            >
              <option value=''>Select Subject</option>
              {subjectList.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </Input>
            <FormText color='danger'>{errorMessage.subject_id}</FormText>
          </FormGroup>

          <FormGroup className='col-md-4'>
            <Label>Faculty</Label>
            <Input
              type='select'
              className='custom-select'
              value={timeTableData.faculty_id}
              onChange={onFacultyChange}
            >
              <option value=''>Select Faculty</option>
              {facultyList.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </Input>
            <FormText color='danger'>{errorMessage.faculty_id}</FormText>
          </FormGroup>
        </Row>

        <Button color='primary' className='float-end' onClick={SaveData}>
          Save
        </Button>
      </CardBody>
    </Card>
  );
};

export default AddEditTimeTable;
